<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const duration = ref(null);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    duration.value =
      currentAccount.value?.settings?.inactivity_logout_duration ?? null;
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  if (duration.value && (duration.value < 1 || duration.value > 1440)) {
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.ERROR'));
    return;
  }
  try {
    isSubmitting.value = true;
    await updateAccount(
      { inactivity_logout_duration: duration.value || null },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.NOTE')"
    with-border
  >
    <form class="grid gap-4 max-w-sm" @submit.prevent="handleSubmit">
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.HELP')"
      >
        <NextInput
          v-model="duration"
          type="number"
          min="1"
          max="1440"
          class="w-full"
          :placeholder="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.LABEL')"
        />
      </WithLabel>
      <div>
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_LOGOUT.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
